import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { UserAuthenticatedLayout } from "@/layouts/AuthenticatedLayout/UserAuthenticatedLayout";
import { useDebounce } from "@/modules/form/utils/useDebounce";
import { authenticatedRouting } from "@/modules/router/routings/authenticatedRouting";
import { useApplySearchKeywordFilter } from "@/modules/search/useApplySearchKeywordFilter";
import { useSearchQuery } from "@/modules/search/useSearchQuery";
import { useSetSearchKeyword } from "@/modules/search/useSetSearchKeyword";
import { FirstLoadingPageView } from "@/modules/theme/components/FirstLoadingPageView";
import { Loader } from "@/modules/theme/components/Loader/Loader";
import { PageMeta } from "@/modules/theme/components/PageMeta";
import { SearchInput } from "@/modules/theme/components/SearchInput/SearchInput";
import { Typography } from "@/modules/theme/components/Typography/Typography";
import { useTheme } from "@/modules/theme/hooks/useTheme";

import { EmptyHandbooksView } from "./components/EmptyHandbooksView";
import { HandbooksList } from "./components/HandbooksList";
import { createHandbooksStyles } from "./createHandbooksStyles";
import { useGetHandbooksQuery } from "./hooks/useGetHandbooksQuery";

const translationNamespace = "pages/HandbooksPage";

export const HandbooksPage = () => {
  const { t } = useTranslation(translationNamespace);
  const classes = createHandbooksStyles(useTheme());
  const { search } = useLocation();

  const query: URLSearchParams = useSearchQuery(search);

  const { searchedKeyword, setSearchedKeyword } = useSetSearchKeyword(query);
  const [isSearchTouched, setIsSearchTouched] = useState(false);

  const debouncedKeywordValue = useDebounce(searchedKeyword, 500);

  useApplySearchKeywordFilter({
    query,
    searchedKeyword: debouncedKeywordValue,
    nextPath: authenticatedRouting.buildUrl("handbooks"),
  });

  const {
    data: handbooks,
    isFetching: isHandbooksDataFetching,
  } = useGetHandbooksQuery(
    isSearchTouched ? debouncedKeywordValue : searchedKeyword
  );

  const isHandbooksListEmpty =
    !searchedKeyword &&
    !debouncedKeywordValue &&
    !handbooks?.length &&
    !isHandbooksDataFetching;

  if (isHandbooksDataFetching && !isSearchTouched) {
    return <FirstLoadingPageView heading={t("Handbook")} />;
  }

  if (isHandbooksListEmpty) {
    return <EmptyHandbooksView />;
  }

  return (
    <UserAuthenticatedLayout containerClassName={classes.root}>
      <PageMeta title={t("Handbook")} />
      <Typography variant="pageHeading" className={classes.heading}>
        {t("Handbook")}
      </Typography>
      <div className={classes.panelWrapper}>
        <SearchInput
          id="searchKeyword"
          placeholder={t("Search...")}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSearchedKeyword(e.target.value);
            setIsSearchTouched(true);
          }}
          value={searchedKeyword}
        />
      </div>
      {isHandbooksDataFetching ? (
        <div className={classes.loaderWrapper}>
          <Loader size={70} variant="primary" />
        </div>
      ) : (
        <HandbooksList handbooks={handbooks} />
      )}
    </UserAuthenticatedLayout>
  );
};
